import { FC, useEffect, useRef, useState } from 'react'
import {
  Button,
  Card,
  Collapse,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Popover,
  Slider,
  Switch,
  Tag,
  Tooltip,
} from 'antd'
import { CloseOutlined, SettingOutlined } from '@ant-design/icons'
import {
  ContainerOutlined,
  DatabaseOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  HistoryOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  PlusOutlined,
  ReloadOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons'
import { UserPicker } from '@/components/admin/UserPicker'
import { searchPoolUsers } from '@/api/poolAdminApi'
import { formatRelative } from '@/utils/time'
import { HarnessSessionItem, jingqueAdminApi } from '@/api/jingqueAdminApi'
import { toast } from '@/utils/toast'
import { TimelineItem, ToolCallItem } from '@/components/admin/harness/timeline'
import { ToolCallCard } from '@/components/admin/harness/ToolCallCard'
import {
  QuestionCard,
  QuestionSummaryCard,
  QUESTION_TOOL_NAME,
  QuestionAnswersPayload,
} from '@/components/admin/harness/QuestionCard'
import { ReasoningBlock } from '@/components/admin/harness/ReasoningBlock'
import { EventBanner } from '@/components/admin/harness/EventBanner'
import { ChatComposer } from '@/components/admin/harness/ChatComposer'
import { FsTreeSidebar } from '@/components/admin/harness/FsTreeSidebar'
import { FilePeekCard } from '@/components/admin/harness/FilePeekCard'
import { CompactionBanner } from '@/components/admin/harness/CompactionBanner'
import { useAgentHarness } from '@/components/admin/harness/AgentHarnessContext'
import {
  Message,
  MessageContent,
  MessageResponse,
} from '@/components/ai-elements/message'
import { Shimmer } from '@/components/ai-elements/shimmer'
import { FoldVertical } from 'lucide-react'
import { cn } from '@/lib/utils'

// 与 AdminLayout header 同源 — 空状态圆形 logo
const JINGQUE_LOGO = 'https://middleiman-public.oss-cn-hangzhou.aliyuncs.com/jingque_avatar.webp'

/** 运行者身份昵称解析:admin 自身读 auth_user(同步零请求),池用户按 ID 查一次并缓存 */
const useUserLabel = (userId: string): string => {
  const [label, setLabel] = useState('')
  useEffect(() => {
    if (!userId) {
      setLabel('')
      return
    }
    try {
      const u = JSON.parse(localStorage.getItem('auth_user') || '{}')
      if (u.user_id === userId) {
        setLabel(u.nickname || u.username || '')
        return
      }
    } catch {
      /* noop */
    }
    let cancelled = false
    searchPoolUsers(userId)
      .then(r => {
        if (cancelled) return
        const hit = (r.users || []).find(x => x.user_id === userId)
        setLabel(hit?.nickname || userId.slice(0, 8))
      })
      .catch(() => {
        if (!cancelled) setLabel(userId.slice(0, 8))
      })
    return () => {
      cancelled = true
    }
  }, [userId])
  return label
}

/** 拖拽把手 pointer 事件(由 AgentFloatWidget 实现位移,透传给 popup 头部) */
export interface PanelDragProps {
  onPointerDown: (e: React.PointerEvent) => void
  onPointerMove: (e: React.PointerEvent) => void
  onPointerUp: (e: React.PointerEvent) => void
  onPointerCancel: (e: React.PointerEvent) => void
}

/**
 * Agent harness 展示层:page(整页三栏,与原路由页一致)/ popup(悬浮弹窗:可收起会话栏 + 指标收头部 Popover)。
 * 状态全部来自 AgentHarnessContext,本文件纯渲染。
 */
export const AgentHarnessPanel: FC<{
  variant: 'page' | 'popup'
  onCollapse?: () => void
  dragProps?: PanelDragProps
}> = ({ variant, onCollapse, dragProps }) => {
  if (variant === 'popup') return <PopupLayout onCollapse={onCollapse} dragProps={dragProps} />
  return <PageLayout />
}

// ───────────────────────── page 变体(原 /agent-harness 整页布局,零视觉变化) ─────────────────────────

const PageLayout: FC = () => {
  const h = useAgentHarness()
  return (
    <div style={{ padding: 24 }}>
      {/* doc 105:中列 minmax(0,1fr) — grid 子项默认 min-width:auto,长内容(工具返回/代码块)会撑爆 1fr 轨道向右推 */}
      <div style={{ display: 'grid', gridTemplateColumns: '280px minmax(0, 1fr) 200px', gap: 16 }}>
        {/* 会话区(doc 104:原「配置」卡片瘦身 — 只留会话列表 + 模板) */}
        <Card
          size="small"
          title="会话"
          loading={h.loadingMeta}
          extra={
            <Button size="small" type="text" icon={<PlusOutlined />} onClick={h.newSession}>
              新建会话
            </Button>
          }
        >
          <Form layout="vertical" size="small">
            <SessionsBlock maxHeight={320} />
          </Form>
        </Card>

        {/* 时间线(doc 104:标题=当前会话标题;extra 只留连接器;状态/停止由 composer 表达)
            doc 108:body 右 padding 归零 → 滚动条贴卡片右缘;composer 自行补偿右距 */}
        <Card
          size="small"
          styles={{ body: { padding: '12px 0 12px 12px' } }}
          title={
            <span className="inline-block max-w-[360px] truncate align-middle" title={h.currentTitle}>
              {h.currentTitle}
            </span>
          }
          extra={
            <span className="inline-flex items-center gap-1">
              <HeaderSettingsPopover />
              <ConnectorTag />
            </span>
          }
        >
          <ChatBlock
            scrollStyle={{ height: 'calc(100vh - 320px)', minHeight: 400, overflowY: 'auto', padding: '8px 14px 8px 4px' }}
          />
        </Card>

        {/* 窄指标栏 */}
        <Card size="small" title="指标">
          <HarnessMetrics />
        </Card>
      </div>
    </div>
  )
}

// ───────────────────────── popup 变体(悬浮弹窗:会话栏可收起,指标收头部) ─────────────────────────

const PopupLayout: FC<{ onCollapse?: () => void; dragProps?: PanelDragProps }> = ({
  onCollapse,
  dragProps,
}) => {
  const h = useAgentHarness()
  // doc 122:左栏改 VFS 文件树;会话列表迁 header 弹窗;文件点击出 Peek 预览
  const [fsOpen, setFsOpen] = useState(true)
  const [sessModalOpen, setSessModalOpen] = useState(false)
  const [peekPath, setPeekPath] = useState('')

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-2xl border bg-background shadow-2xl">
      {/* 头部:文件栏开关 / 标题 / 会话列表弹窗 / token 指标 / 新建 / 收起;空白处可拖动窗口 */}
      <div
        className="flex h-11 shrink-0 cursor-move select-none items-center gap-1 border-b px-2"
        {...dragProps}
      >
        <Tooltip title={fsOpen ? '收起文件面板' : '展开文件面板'}>
          <Button
            type="text"
            size="small"
            icon={fsOpen ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
            onClick={() => setFsOpen(v => !v)}
          />
        </Tooltip>
        <span className="min-w-0 flex-1 truncate text-sm font-medium" title={h.currentTitle}>
          {h.currentTitle}
        </span>
        <Tooltip title="会话列表">
          <Button
            type="text"
            size="small"
            icon={<HistoryOutlined />}
            onClick={() => {
              h.loadSessions()
              setSessModalOpen(true)
            }}
          />
        </Tooltip>
        <HeaderTokenMetrics />
        <Tooltip title="压缩上下文">
          <Button
            type="text"
            size="small"
            icon={<FoldVertical className="size-4" />}
            loading={h.running && !!h.liveCompaction}
            disabled={!h.contextId || h.anyRunning || h.hasPendingTool}
            onClick={() => h.runCompaction()}
          />
        </Tooltip>
        <HeaderSettingsPopover />
        <Tooltip title="新建会话">
          <Button type="text" size="small" icon={<PlusOutlined />} onClick={h.newSession} />
        </Tooltip>
        <Tooltip title="收起到悬浮球">
          <Button type="text" size="small" icon={<DownOutlined />} onClick={onCollapse} />
        </Tooltip>
      </div>

      {/* doc 120:tab 栏(仅 popup)— 多会话并行,后台流式不断 */}
      <TabStrip />

      {/* 主体:可收起文件树栏 + 对话区(Peek 浮卡盖在对话区上层) */}
      <div className="flex min-h-0 flex-1">
        {fsOpen && (
          <div className="w-[232px] shrink-0 border-r">
            <FsTreeSidebar onOpenFile={setPeekPath} />
          </div>
        )}
        <div className="relative flex min-w-0 flex-1 flex-col">
          <ChatBlock
            scrollClassName="flex-1"
            scrollStyle={{ overflowY: 'auto', padding: '8px 14px 8px 12px' }}
            composerMaxWidth={640}
          />
          {peekPath && (
            <FilePeekCard
              file={{ path: peekPath, contextId: h.contextId, runAs: h.runAsUserId, templateId: h.templateId }}
              onClose={() => setPeekPath('')}
            />
          )}
        </div>
      </div>

      {/* doc 122:会话列表弹窗(原左栏内容)— 无遮罩不挡对话;zIndex 1030 高于面板(1020);选中会话后自动关闭 */}
      <Modal
        open={sessModalOpen}
        title="会话列表"
        footer={null}
        width={400}
        zIndex={1030}
        mask={false}
        onCancel={() => setSessModalOpen(false)}
      >
        <SessionsBlock maxHeight={480} onNavigate={() => setSessModalOpen(false)} />
      </Modal>
    </div>
  )
}

// ───────────────────────── 共享子块 ─────────────────────────

/** doc 123-Fix:压缩流式卡 — 摘要逐 chunk 显示,完成后由时间线 CompactionItem 接班 */
const LiveCompactionCard: FC<{ content: string; trigger: string }> = ({ content, trigger }) => {
  const label = '正在整理历史上下文…'
  return (
    <div className="my-2 shrink-0 overflow-hidden rounded-md border bg-muted/40 text-xs">
      <div className="flex items-center gap-2 px-3 py-1.5 text-muted-foreground">
        <FoldVertical className="size-4 shrink-0" />
        <span className="grow">{label}</span>
        <span className="text-[10px] opacity-60">{trigger}</span>
      </div>
      {content && (
        <div className="max-h-40 overflow-y-auto whitespace-pre-wrap break-words border-t bg-background/50 px-3 py-2 leading-relaxed text-foreground/85">
          {content}
        </div>
      )}
    </div>
  )
}

/** popup 头部内联 token 指标:上下文(压缩占比) + 累计,点击展开完整明细 Popover;
 *  doc 123/124:压缩占比并入括号,超阈值变橙(原 CompactionBadge) */
const HeaderTokenMetrics: FC = () => {
  const h = useAgentHarness()
  const s = h.compactionStatus
  const pct = s ? Math.round(s.ratio * 100) : null
  const warn = s ? s.ratio >= s.trigger_ratio : false
  return (
    <Popover
      trigger="click"
      placement="bottomRight"
      content={
        <div style={{ width: 240 }}>
          <HarnessMetrics />
        </div>
      }
    >
      <button
        type="button"
        title="指标明细"
        className="flex shrink-0 cursor-pointer items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] text-muted-foreground transition-colors hover:bg-muted"
      >
        <span className="tabular-nums">
          上下文 {h.contextTokens ? h.contextTokens.toLocaleString() : '-'}
          {pct !== null && (
            <Tooltip title={`阈值 ${Math.round(s!.trigger_ratio * 100)}%, 基于 ${s!.basis === 'real' ? '真实 provider tokens' : '估算'}`}>
              <span className={cn('tabular-nums', warn && 'text-orange-600 dark:text-orange-400')}>
                {' '}({pct}%)
              </span>
            </Tooltip>
          )}
        </span>
        <span className="opacity-40">·</span>
        <span className="tabular-nums">累计 {h.totalTokens.toLocaleString()}</span>
      </button>
    </Popover>
  )
}

/** connector 双态 Tag:显式 override → 紫「指定」;否则蓝=模板 connector */
const ConnectorTag: FC = () => {
  const h = useAgentHarness()
  if (h.connectorId) {
    return (
      <Tag color="purple" className="mr-0">
        {h.connectors.find(c => c.connectorId === h.connectorId)?.connectorName || h.connectorId} · 指定
      </Tag>
    )
  }
  if (!h.tpl) return null
  return (
    <Tag color="blue" className="mr-0">
      {h.tplConnector?.connectorName || h.tpl.connectorId || '-'}
    </Tag>
  )
}

/** doc 124:header 快速设置 — 压缩配置(Redis 全局,GET/PATCH 现成)+ run-as 视角。
 *  改动即 PATCH 保存(agent 端钳制);打开 Popover 时载入;保存后刷 CompactionBadge。 */
const HeaderSettingsPopover: FC = () => {
  const h = useAgentHarness()
  const userLabel = useUserLabel(h.runAsUserId)
  const [cfg, setCfg] = useState<{
    enabled: boolean
    trigger_ratio: number
    keep_tokens: number
    min_messages: number
    cooldown_messages: number
  } | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const load = async () => {
    setLoading(true)
    try {
      setCfg(await jingqueAdminApi.agentHarness.compactionConfigGet())
    } catch (e: any) {
      toast.error('加载压缩配置失败: ' + (e.message || String(e)))
    } finally {
      setLoading(false)
    }
  }

  const save = async (partial: Partial<NonNullable<typeof cfg>>) => {
    if (!cfg) return
    const prev = cfg
    setCfg({ ...cfg, ...partial }) // 乐观更新,失败回滚
    setSaving(true)
    try {
      setCfg(await jingqueAdminApi.agentHarness.compactionConfigSet(partial))
      toast.success('压缩设置已保存', 1500)
      h.refreshCompactionStatus()
    } catch (e: any) {
      setCfg(prev)
      toast.error('保存失败: ' + (e.message || String(e)))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Popover
      trigger="click"
      placement="bottomRight"
      onOpenChange={open => {
        if (open) void load()
      }}
      content={
        <div style={{ width: 280 }}>
          {/* 压缩设置 — 全局 Redis 配置 */}
          <div className="mb-1 text-xs font-medium text-muted-foreground">上下文压缩</div>
          {cfg ? (
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-xs">自动压缩</span>
                <Switch size="small" checked={cfg.enabled} loading={saving} onChange={v => save({ enabled: v })} />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">触发阈值 {Math.round(cfg.trigger_ratio * 100)}%</div>
                <Slider
                  min={30}
                  max={90}
                  step={5}
                  value={Math.round(cfg.trigger_ratio * 100)}
                  disabled={!cfg.enabled || saving}
                  onChange={v => setCfg({ ...cfg, trigger_ratio: v / 100 })}
                  onChangeComplete={v => save({ trigger_ratio: v / 100 })}
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs">保留 tokens</span>
                <InputNumber
                  size="small"
                  min={1024}
                  step={1024}
                  value={cfg.keep_tokens}
                  disabled={!cfg.enabled || saving}
                  onChange={v => v && save({ keep_tokens: v })}
                  style={{ width: 100 }}
                />
              </div>
              <Collapse
                ghost
                size="small"
                items={[
                  {
                    key: 'adv',
                    label: <span className="text-xs">高级</span>,
                    children: (
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs">最少消息数</span>
                          <InputNumber
                            size="small"
                            min={2}
                            value={cfg.min_messages}
                            disabled={!cfg.enabled || saving}
                            onChange={v => v && save({ min_messages: v })}
                            style={{ width: 80 }}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs">冷却消息数</span>
                          <InputNumber
                            size="small"
                            min={0}
                            value={cfg.cooldown_messages}
                            disabled={!cfg.enabled || saving}
                            onChange={v => v !== null && save({ cooldown_messages: v })}
                            style={{ width: 80 }}
                          />
                        </div>
                      </div>
                    ),
                  },
                ]}
              />
              <div className="text-[10px] leading-snug text-muted-foreground">
                全局设置,对所有会话生效;约 5 秒内生效(agent 配置快照)
              </div>
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">{loading ? '加载中…' : '加载失败'}</div>
          )}

          {/* run-as 查询视角 */}
          <div className="mb-1 mt-3 border-t pt-2 text-xs font-medium text-muted-foreground">查询视角</div>
          <UserPicker
            value={h.runAsUserId || undefined}
            valueLabel={userLabel}
            onChange={v => h.setRunAsUserId(v || '')}
            placeholder="默认本人,可搜索切换"
            allowClear
            width="100%"
          />
        </div>
      }
    >
      <Button type="text" size="small" icon={<SettingOutlined />} title="快速设置" />
    </Popover>
  )
}

/** 会话块:admin 会话列表(page/popup 复用,不带 Card 壳);
 *  onNavigate:doc 122 弹窗模式选中会话后回调(关闭弹窗)。
 *  doc 124:run-as 查询视角已迁 header 快速设置(HeaderSettingsPopover),此处只留刷新。 */
const SessionsBlock: FC<{ maxHeight: number | undefined; onNavigate?: () => void }> = ({
  maxHeight,
  onNavigate,
}) => {
  const h = useAgentHarness()
  return (
    <>
      <div className="mb-1 flex items-center justify-end">
        <Tooltip title="刷新会话列表">
          <Button
            type="text"
            size="small"
            icon={<ReloadOutlined style={{ fontSize: 11 }} />}
            onClick={() => h.loadSessions()}
          />
        </Tooltip>
      </div>

      {/* 97:会话列表 — admin 名下的 voicechat 会话(doc 104:隐藏 0 条;doc 121:与 run-as 无关) */}
      <div
        className="flex flex-col gap-0.5 overflow-y-auto"
        style={maxHeight ? { maxHeight } : undefined}
      >
        {h.sessionsLoading && (
          <div className="py-3 text-center text-xs text-muted-foreground">加载中…</div>
        )}
        {!h.sessionsLoading && h.visibleSessions.length === 0 && (
          <div className="py-3 text-center text-xs text-muted-foreground">尚无会话</div>
        )}
        {h.visibleSessions.map(s => (
          <SessionRow key={s.session_id} s={s} onNavigate={onNavigate} />
        ))}
      </div>
    </>
  )
}

const SessionRow: FC<{ s: HarnessSessionItem; onNavigate?: () => void }> = ({ s, onNavigate }) => {
  const h = useAgentHarness()
  const isCurrent = s.session_id === h.contextId
  const display = s.title || s.session_id
  const time = formatRelative(s.last_active_at)
  return (
    <div
      className={cn(
        'group/sess relative flex items-center gap-1 rounded-md px-2 py-1.5 transition-colors',
        isCurrent
          ? 'bg-muted font-medium'
          : 'hover:bg-muted/50',
      )}
    >
      {/* 激活指示条 */}
      {isCurrent && (
        <span className="absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-full bg-primary" />
      )}
      <div
        onClick={() => {
          if (!isCurrent) {
            h.openSession(s)
            onNavigate?.()
          }
        }}
        className={cn(
          'min-w-0 flex-1 cursor-pointer truncate text-xs',
          isCurrent && 'pointer-events-none',
        )}
        title={display}
      >
        {h.renamingId === s.session_id ? (
          <Input
            autoFocus
            size="small"
            value={h.renamingValue}
            onChange={e => h.setRenamingValue(e.target.value)}
            onPressEnter={() => h.submitRename(s.session_id)}
            onBlur={() => h.submitRename(s.session_id)}
            onClick={e => e.stopPropagation()}
          />
        ) : (
          display
        )}
      </div>
      {/* 右侧时间(hover 时隐去让给操作组) */}
      <span className="shrink-0 text-[10px] text-muted-foreground transition-opacity group-hover/sess:opacity-0">
        {time}
      </span>
      {/* 悬停时操作组原位浮现 */}
      <div className="absolute right-1 flex shrink-0 items-center gap-0 opacity-0 transition-opacity group-hover/sess:opacity-100">
        {h.renamingId !== s.session_id && (
          <Tooltip title="重命名">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined style={{ fontSize: 11 }} />}
              onClick={() => {
                h.setRenamingId(s.session_id)
                h.setRenamingValue(s.title || '')
              }}
            />
          </Tooltip>
        )}
        <Popconfirm
          title="删除该会话及其消息?"
          okText="删除"
          cancelText="取消"
          okButtonProps={{ danger: true }}
          onConfirm={() => h.removeSession(s.session_id)}
        >
          <Tooltip title="删除">
            <Button type="text" size="small" danger icon={<DeleteOutlined style={{ fontSize: 11 }} />} />
          </Tooltip>
        </Popconfirm>
      </div>
    </div>
  )
}

/** 对话块:时间线滚动区 + composer(page/popup 复用);composerMaxWidth 限宽居中(popup 防满宽) */
const ChatBlock: FC<{
  scrollClassName?: string
  scrollStyle: React.CSSProperties
  composerMaxWidth?: number
}> = ({ scrollClassName, scrollStyle, composerMaxWidth }) => {
  const h = useAgentHarness()
  const scrollRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [h.items, h.liveCompaction?.content])

  return (
    <>
      {/* doc 108:右 padding — 滚动条贴右缘,气泡与滚动条间留呼吸位 */}
      <div
        ref={scrollRef}
        className={cn('flex flex-col gap-3.5', h.items.length === 0 && 'justify-center', scrollClassName)}
        style={scrollStyle}
      >
        {h.items.length === 0 && !h.running && (
          <div className="flex flex-col items-center gap-3">
            <img src={JINGQUE_LOGO} alt="菁鹊" className="size-36 rounded-full object-cover" />
            <span className="text-xs text-muted-foreground">还没有消息</span>
          </div>
        )}
        {h.items.map(item => (
          <TimelineRenderer
            key={item.key}
            item={item}
            streaming={h.running && item.key === h.liveKeyRef.current}
            onExecute={h.runFrontendTool}
            onSubmitQuestion={h.submitQuestion}
          />
        ))}
        {/* doc 123-Fix:压缩流式卡 — 摘要逐 chunk 显示,完成后由时间线 CompactionItem 接班 */}
        {h.liveCompaction && (
          <LiveCompactionCard content={h.liveCompaction.content} trigger={h.liveCompaction.trigger} />
        )}
        {/* 等待首个 chunk / 工具执行期间的占位(压缩回合被流式卡接班,不叠指示) */}
        {h.running && !h.liveCompaction && !h.items.some(i => i.key === h.liveKeyRef.current) && (
          <Shimmer>正在生成…</Shimmer>
        )}
      </div>
      {/* doc 108:page 变体 composer 补偿右距 12px;popup 变体 max-width 居中 + 均衡 padding */}
      <div style={composerMaxWidth ? { padding: '0 16px 12px' } : { paddingRight: 12 }}>
        <div style={composerMaxWidth ? { maxWidth: composerMaxWidth, margin: '0 auto' } : undefined}>
          <ChatComposer
            disabled={!h.tpl || h.hasPendingTool}
            running={h.running}
            templates={h.templates.map(t => ({ templateId: t.templateId, name: t.name }))}
            templateId={h.templateId}
            onTemplateChange={h.setTemplateId}
            connectors={h.connectors.map(c => ({ connectorId: c.connectorId, connectorName: c.connectorName }))}
            connectorId={h.connectorId}
            onConnectorChange={h.setConnectorId}
            templateConnectorName={h.tplConnector?.connectorName || h.tpl?.connectorId || ''}
            thinking={!h.disableThinking}
            onThinkingChange={v => h.setDisableThinking(!v)}
            tools={h.tools.map(t => ({ toolName: t.toolName, executionLocation: t.executionLocation }))}
            selectedTools={h.selectedToolNames}
            onToolsChange={h.setSelectedToolNames}
            onSend={h.send}
            onStop={h.stop}
          />
        </div>
      </div>
    </>
  )
}

/** 指标块(page 右栏 Card / popup 头部 Popover 复用) */
const HarnessMetrics: FC = () => {
  const h = useAgentHarness()
  const total3 = h.cumMissTokens + h.cumCacheTokens + h.cumOutputTokens
  const inputTotal = h.cumMissTokens + h.cumCacheTokens
  const hitRate = inputTotal > 0 ? (h.cumCacheTokens / inputTotal) * 100 : 0
  const pct = (v: number) => `${(v / total3) * 100}%`
  return (
    <div className="flex flex-col gap-3">
      {/* doc 115:两个头部指标 —— 上下文体积(最新轮 prompt)与累计消耗 */}
      <MetricRow icon={<ContainerOutlined />} label="当前上下文" value={h.contextTokens || '-'} />
      <MetricRow icon={<DatabaseOutlined />} label="累计 token" value={h.totalTokens} />

      {/* doc 115:构成 —— 三分明细为最重要信息,独立子卡 + 堆叠比例条 */}
      {total3 > 0 && (
        <div className="rounded-lg border border-border bg-muted/40 p-3">
          <div className="flex items-baseline justify-between mb-2">
            <span className="text-xs font-medium text-muted-foreground">构成</span>
            <span className="text-xs text-muted-foreground tabular-nums">
              输入缓存命中率 <span className="font-semibold text-emerald-600">{hitRate.toFixed(1)}%</span>
            </span>
          </div>
          {/* 堆叠比例条:rose=未命中输入(全价) emerald=缓存命中 sky=输出 */}
          <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted mb-3">
            <div className="bg-rose-400" style={{ width: pct(h.cumMissTokens) }} />
            <div className="bg-emerald-500" style={{ width: pct(h.cumCacheTokens) }} />
            <div className="bg-sky-400" style={{ width: pct(h.cumOutputTokens) }} />
          </div>
          <div className="flex flex-col gap-1.5">
            <BreakdownRow dot="bg-rose-400" label="未命中输入" value={h.cumMissTokens} />
            <BreakdownRow dot="bg-emerald-500" label="缓存命中" value={h.cumCacheTokens} />
            <BreakdownRow dot="bg-sky-400" label="输出" value={h.cumOutputTokens} />
          </div>
          {h.legacyNoDetail > 0 && (
            <div className="text-xs text-muted-foreground/70 mt-2">
              含 {h.legacyNoDetail} 条无明细历史(仅计入总数)
            </div>
          )}
        </div>
      )}

      <MetricRow icon={<UnorderedListOutlined />} label="条目数" value={h.items.length} />
    </div>
  )
}

// ── 渲染映射:kind → 组件 ──
const TimelineRenderer: FC<{
  item: TimelineItem
  streaming: boolean
  onExecute: (item: ToolCallItem) => void
  onSubmitQuestion: (item: ToolCallItem, payload: QuestionAnswersPayload) => void
}> = ({ item, streaming, onExecute, onSubmitQuestion }) => {
  switch (item.kind) {
    case 'user':
      // doc 105:条目间距由容器 gap 统一管理,不再自带 mb
      return (
        <Message from="user">
          <MessageContent className="group-[.is-user]:rounded-br-sm">
            <div className="whitespace-pre-wrap break-words">{item.content}</div>
          </MessageContent>
        </Message>
      )
    case 'assistant':
      return (
        <>
          {item.reasoning ? <ReasoningBlock text={item.reasoning} streaming={streaming} /> : null}
          {item.content ? (
            <MessageResponse>{item.content}</MessageResponse>
          ) : streaming && !item.reasoning ? (
            <Shimmer>正在生成…</Shimmer>
          ) : null}
        </>
      )
    case 'tool_call':
      // doc 118:question 专属渲染 — pending 交互答题 / success 摘要(含历史)
      if (item.name === QUESTION_TOOL_NAME) {
        if (item.status === 'pending' && item.location === 'frontend') {
          return <QuestionCard item={item} onSubmit={p => onSubmitQuestion(item, p)} />
        }
        if (item.status === 'success') {
          return <QuestionSummaryCard item={item} />
        }
      }
      return <ToolCallCard item={item} onExecute={onExecute} />
    case 'event':
      return <EventBanner eventType={item.eventType} title={item.title} detail={item.detail} />
    case 'compaction':
      return <CompactionBanner item={item} />
    default:
      return null
  }
}

/** 指标栏紧凑行:图标 + 标签 + 数值。value 支持数字或 "-" 占位。 */
const MetricRow: FC<{ icon: React.ReactNode; label: string; value: number | string }> = ({
  icon,
  label,
  value,
}) => (
  <div className="flex items-center gap-2.5">
    <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
      {icon}
    </div>
    <div className="min-w-0 flex-1 text-xs text-muted-foreground">{label}</div>
    <div className="text-base font-semibold tabular-nums">
      {typeof value === 'number' ? value.toLocaleString() : value}
    </div>
  </div>
)

/** 构成明细行:色点 + 标签 + 数值(与堆叠条颜色一一对应)。 */
const BreakdownRow: FC<{ dot: string; label: string; value: number }> = ({ dot, label, value }) => (
  <div className="flex items-center gap-2">
    <span className={`size-2 shrink-0 rounded-full ${dot}`} />
    <span className="flex-1 text-xs text-muted-foreground">{label}</span>
    <span className="text-sm font-medium tabular-nums">{value.toLocaleString()}</span>
  </div>
)

/** doc 120:tab 栏(浏览器样式)— 激活 tab 白底浮起与内容连成一体;running 呼吸点表示后台在跑 */
const TabStrip: FC = () => {
  const h = useAgentHarness()
  return (
    <div className="flex h-9 shrink-0 items-end gap-0.5 overflow-x-auto border-b bg-muted/30 pl-1.5 pr-1">
      {h.tabs.map(t => {
        const isActive = t.tabId === h.activeTabId
        return (
          <div
            key={t.tabId}
            onClick={() => h.activateTab(t.tabId)}
            title={t.title}
            className={cn(
              'group/tab relative flex h-8 min-w-0 max-w-[180px] shrink-0 cursor-pointer select-none items-center gap-1.5 rounded-t-md pl-3 pr-1.5 text-xs transition-colors',
              isActive
                ? 'border-x border-t bg-background font-medium text-foreground'
                : 'text-muted-foreground hover:bg-background/60 hover:text-foreground',
            )}
          >
            {/* 激活 tab 与下方内容连成一体:盖住 strip 的底边框 */}
            {isActive && <span className="absolute inset-x-0 -bottom-px h-px bg-background" />}
            {t.running && (
              <span className="relative flex size-1.5 shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex size-1.5 rounded-full bg-emerald-500" />
              </span>
            )}
            <span className="truncate">{t.title}</span>
            <button
              type="button"
              onClick={e => {
                e.stopPropagation()
                h.closeTab(t.tabId)
              }}
              title={t.running ? '流式进行中,关闭将停止' : '关闭 tab'}
              className={cn(
                'flex size-4 shrink-0 items-center justify-center rounded-full transition-all hover:bg-muted-foreground/20',
                isActive ? 'opacity-60 hover:opacity-100' : 'opacity-0 group-hover/tab:opacity-60',
              )}
            >
              <CloseOutlined style={{ fontSize: 9 }} />
            </button>
          </div>
        )
      })}
      <Tooltip title="新会话 tab">
        <button
          type="button"
          onClick={() => h.openTab()}
          className="mb-0.5 ml-0.5 flex size-7 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
        >
          <PlusOutlined style={{ fontSize: 12 }} />
        </button>
      </Tooltip>
    </div>
  )
}